import React, { useEffect } from "react";
import { Flame } from "lucide-react";
import { inr } from "../lib/format";

const FILL = { red: "#dc2626", black: "#18181b", green: "#16a34a" };

function playWinSound(streak) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const notes = streak >= 2
      ? [523.25, 659.25, 783.99, 1046.5, 1318.5]  // big rising fanfare
      : [659.25, 987.77];                          // short ding
    notes.forEach((f, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "triangle";
      o.frequency.value = f;
      const t = ctx.currentTime + i * 0.11;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(streak >= 2 ? 0.28 : 0.18, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
      o.connect(g); g.connect(ctx.destination);
      o.start(t); o.stop(t + 0.55);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1800);
  } catch { /* audio blocked — celebration still shows */ }
}

// A short celebratory overlay that reveals the winning number/result.
// Back-to-back wins (streak >= 2) get a bigger, longer celebration + fanfare.
// No negative messaging is ever shown — losses simply reveal the number.
export function WinReveal({ open, label, color, won, payout, streak = 0, onClose }) {
  const hot = won && streak >= 2;

  useEffect(() => {
    if (!open) return;
    if (won) playWinSound(streak);
    const t = setTimeout(() => onClose && onClose(), hot ? 3000 : 2000);
    return () => clearTimeout(t);
  }, [open, won, streak, hot, onClose]);

  if (!open) return null;

  const ring = hot ? "#facc15" : color ? FILL[color] : "#facc15";
  const dur = hot ? "3s" : "2s";
  const sparks = hot ? 26 : 14;

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center pointer-events-none" data-testid="win-reveal">
      <style>{`
        @keyframes wr-pop { 0%{transform:scale(.5);opacity:0} 55%{transform:scale(1.12);opacity:1} 100%{transform:scale(1);opacity:1} }
        @keyframes wr-fade { 0%{opacity:0} 12%{opacity:1} 82%{opacity:1} 100%{opacity:0} }
        @keyframes wr-spark { 0%{transform:translate(0,0) scale(0);opacity:0} 20%{opacity:1} 100%{transform:translate(var(--dx),var(--dy)) scale(1);opacity:0} }
        @keyframes wr-ringwave { 0%{transform:scale(.6);opacity:.9} 100%{transform:scale(2.1);opacity:0} }
        @keyframes wr-flicker { 0%,100%{transform:scale(1)} 50%{transform:scale(1.15)} }
      `}</style>
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" style={{ animation: `wr-fade ${dur} ease forwards` }} />

      <div className="relative" style={{ animation: `wr-fade ${dur} ease forwards` }}>
        {/* sparkles */}
        {Array.from({ length: sparks }).map((_, i) => {
          const ang = (i / sparks) * Math.PI * 2;
          const dist = (hot ? 120 : 90) + (i % 3) * (hot ? 34 : 22);
          return (
            <span key={i} className="absolute left-1/2 top-1/2 h-2 w-2 rounded-full"
              style={{
                background: i % 2 ? "#facc15" : "#fde68a",
                "--dx": `${Math.cos(ang) * dist}px`,
                "--dy": `${Math.sin(ang) * dist}px`,
                animation: `wr-spark ${hot ? 1.8 : 1.4}s ease-out ${(i % 5) * 0.05}s forwards`,
                boxShadow: "0 0 8px rgba(250,204,21,0.9)",
              }} />
          );
        })}

        {/* golden shockwave rings on streak */}
        {hot && [0, 0.25].map((d) => (
          <span key={d} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              height: 160, width: 160,
              border: "3px solid rgba(250,204,21,0.8)",
              animation: `wr-ringwave 1.3s ease-out ${d}s forwards`,
              boxShadow: "0 0 24px rgba(250,204,21,0.6)",
            }} />
        ))}

        <div className="relative flex flex-col items-center" style={{ animation: "wr-pop .5s cubic-bezier(.2,.9,.25,1.2) both" }}>
          {hot && (
            <div className="mb-3 flex items-center gap-1.5 rounded-full px-4 py-1.5 font-display font-black text-black text-sm uppercase tracking-wide"
              data-testid="win-streak-banner"
              style={{
                background: "linear-gradient(135deg,#fde68a,#f59e0b 55%,#d97706)",
                boxShadow: "0 0 24px rgba(245,158,11,0.9)",
                animation: "wr-flicker 0.7s ease-in-out infinite",
              }}>
              <Flame className="h-4 w-4" /> {streak} wins in a row!
            </div>
          )}
          <div className="grid place-items-center rounded-full font-display font-black text-white"
            data-testid="win-reveal-number"
            style={{
              height: hot ? 150 : 128, width: hot ? 150 : 128,
              background: color ? FILL[color] : "#0b0f14",
              fontSize: String(label).length > 2 ? (hot ? 50 : 44) : (hot ? 64 : 56),
              border: `4px solid ${ring}`,
              boxShadow: `0 0 ${hot ? 60 : 40}px ${ring}, inset 0 0 22px rgba(0,0,0,0.35)`,
            }}>
            {label}
          </div>
          {won ? (
            <div className={`mt-4 rounded-full bg-primary px-5 py-1.5 text-black font-extrabold shadow-lg ${hot ? "text-lg" : "text-base"}`} data-testid="win-reveal-payout">
              Won {inr(payout)} 🎉
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
